import { takeEvery, call, put, all } from 'redux-saga/effects';
import { gcApi } from 'config/axios';
import * as UrlUtil from 'utils/UrlUtil';
import {
    FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS,
    FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS_FETCHING,
    FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS_SUCCESS,
    FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS_FAIL,
    UPDATE_SLEEP_NOTE,
    UPDATE_SLEEP_NOTE_START,
    UPDATE_SLEEP_NOTE_SUCCESS,
    UPDATE_SLEEP_NOTE_FAIL,
    ADD_SLEEP,
    ADD_SLEEP_START,
    ADD_SLEEP_FAIL,
    ADD_SLEEP_SUCCESS,
    FETCH_NAP_BY_DATE,
    FETCH_NAP_BY_DATE_FETCHING,
    FETCH_NAP_BY_DATE_SUCCESS,
    FETCH_NAP_BY_DATE_FAIL,
    UPDATE_SLEEP_WINDOW,
    UPDATE_SLEEP_WINDOW_START,
    UPDATE_SLEEP_WINDOW_SUCCESS,
    UPDATE_SLEEP_WINDOW_FAIL,
    FETCH_HRV_DAILY,
    FETCH_HRV_DAILY_FETCHING,
    FETCH_HRV_DAILY_SUCCESS,
    FETCH_HRV_DAILY_FAIL,
    ADD_NAP_SUCCESS,
    ADD_NAP_FAIL,
    ADD_NAP,
    ADD_NAP_START,
    DELETE_NAP,
    DELETE_NAP_START,
    DELETE_NAP_SUCCESS,
    DELETE_NAP_FAIL,
    UPDATE_NAP,
    FETCH_DAILY_SLEEP_START,
    FETCH_DAILY_SLEEP_SUCCESS,
    FETCH_DAILY_SLEEP_FAIL
} from '../actions/ActionTypes';

import { fetchMergedSummarySaga } from 'sagas/dailySummarySagas';

// Workers
export function* fetchDailySleepDataWithEpochsSaga(action) {
    try {
        const { calendarDate, nonSleepBufferMinutes } = action;

        yield put({
            type: FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS_FETCHING,
            calendarDate
        });

        const qsObject = {
            date: calendarDate
        };

        if (nonSleepBufferMinutes) {
            qsObject.nonSleepBufferMinutes = nonSleepBufferMinutes;
        }

        const queryParams = UrlUtil.toQueryString(qsObject);

        const response = yield call(gcApi.get, `sleep-service/sleep/dailySleepData${queryParams}`);

        yield put({
            type: FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS_SUCCESS,
            dailySleepData: response.data,
            calendarDate: calendarDate
        });
    } catch (e) {
        yield put({
            type: FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS_FAIL,
            dailySleepData: null,
            calendarDate: action?.calendarDate || null
        });
    }
}

export function* updateSleepNote(action) {
    const { userNote, calendarDate } = action;

    try {
        yield put({
            type: UPDATE_SLEEP_NOTE_START,
            userNote,
            calendarDate
        });

        const response = yield call(gcApi.put, `sleep-service/sleep/dailySleep/${calendarDate}/note`, {
            userNote
        });

        yield put({
            type: UPDATE_SLEEP_NOTE_SUCCESS,
            userNote: response.data
        });
    } catch (e) {
        yield put({
            type: UPDATE_SLEEP_NOTE_FAIL,
            calendarDate
        });
    }
}

export function* addSleep(action) {
    const { sleepObject, calendarDate, nonSleepBufferMinutes } = action;

    try {
        yield put({
            type: ADD_SLEEP_START
        });

        const response = yield call(gcApi.post, `sleep-service/sleep/dailySleep`, sleepObject);
        yield put({
            type: ADD_SLEEP_SUCCESS,
            dailySleepData: response.data,
            calendarDate
        });

        if (response.status === 200) {
            yield all([call(fetchDailySleepDataWithEpochsSaga, { calendarDate, nonSleepBufferMinutes })]);
        } else {
            yield put({
                type: ADD_SLEEP_FAIL,
                calendarDate
            });
        }
    } catch (e) {
        yield put({
            type: ADD_SLEEP_FAIL,
            calendarDate
        });
    }
}

export function* fetchNaps(action) {
    const { calendarDate } = action;

    try {
        yield put({
            type: FETCH_NAP_BY_DATE_FETCHING,
            calendarDate
        });

        const response = yield call(gcApi.get, `sleep-service/sleep/naps/${calendarDate}`);

        yield put({
            type: FETCH_NAP_BY_DATE_SUCCESS,
            dailyNapData: response.data,
            calendarDate
        });
    } catch (e) {
        yield put({
            type: FETCH_NAP_BY_DATE_FAIL,
            calendarDate
        });
    }
}

export function* addNap(action) {
    const { napObject } = action;

    try {
        yield put({
            type: ADD_NAP_START
        });
        const response = yield call(gcApi.post, `sleep-service/sleep/nap`, napObject);

        if (response.status === 200) {
            yield all([call(fetchDailySleep, { calendarDate: napObject.calendarDate })]);
            yield put({
                type: ADD_NAP_SUCCESS
            });
        } else {
            yield put({
                type: ADD_NAP_FAIL
            });
        }
    } catch (e) {
        yield put({
            type: ADD_NAP_FAIL
        });
    }
}

export function* deleteNap(action) {
    try {
        yield put({
            type: DELETE_NAP_START
        });

        const { calendarDate, startTimeInMilis } = action;

        const response = yield call(gcApi.delete, `sleep-service/sleep/nap/${calendarDate}/${startTimeInMilis}`);

        if (response.status === 204) {
            yield all([call(fetchDailySleep, { calendarDate })]);

            yield put({
                type: DELETE_NAP_SUCCESS
            });
        } else {
            yield put({
                type: DELETE_NAP_FAIL,
                calendarDate: action.calendarDate
            });
        }
    } catch (e) {
        yield put({
            type: DELETE_NAP_FAIL,
            calendarDate: action.calendarDate
        });
    }
}

export function* updateNap(action) {
    try {
        yield put({
            type: ADD_NAP_START
        });

        const { calendarDate, startTimeInMilis, endTimeInMilis, updatedNapObject } = action;

        const response = yield call(
            gcApi.put,
            `sleep-service/sleep/nap/${calendarDate}/${startTimeInMilis}/${endTimeInMilis}`,
            updatedNapObject
        );

        if (response.status === 204) {
            yield all([call(fetchDailySleep, { calendarDate })]);

            yield put({
                type: ADD_NAP_SUCCESS
            });
        } else {
            yield put({
                type: ADD_NAP_FAIL
            });
        }
    } catch (e) {
        yield put({
            type: ADD_NAP_FAIL
        });
    }
}

export function* fetchDailySleep(action) {
    const { calendarDate } = action;
    try {
        yield put({
            type: FETCH_DAILY_SLEEP_START
        });

        // fetch only dailySleepDTO data, without epochs
        const response = yield call(gcApi.get, `sleep-service/sleep/dailySleep?date=${calendarDate}`);
        yield put({
            type: FETCH_DAILY_SLEEP_SUCCESS,
            payload: response.data,
            calendarDate: action.calendarDate
        });
    } catch (e) {
        yield put({
            type: FETCH_DAILY_SLEEP_FAIL,
            calendarDate: action.calendarDate
        });
    }
}

export function* updateSleepWindow(action) {
    try {
        const newSleepDTO = action.newSleepDTO;

        yield put({
            type: UPDATE_SLEEP_WINDOW_START
        });

        const response = yield call(gcApi.put, `sleep-service/sleep/dailySleep/${newSleepDTO.id}`, newSleepDTO);

        if (response.status === 200) {
            const { displayName, calendarDate, nonSleepBufferMinutes } = action;
            yield all([
                call(fetchDailySleepDataWithEpochsSaga, { displayName, calendarDate, nonSleepBufferMinutes }),
                call(fetchMergedSummarySaga, { displayName, calendarDate })
            ]);
        } else {
            yield put({
                type: UPDATE_SLEEP_WINDOW_FAIL
            });
        }

        yield put({
            type: UPDATE_SLEEP_WINDOW_SUCCESS,
            dailySleepData: response.data,
            calendarData: action.calendarDate
        });
    } catch (e) {
        yield put({
            type: UPDATE_SLEEP_WINDOW_FAIL
        });
    }
}

export function* fetchHrvDaily(action) {
    const { startDate, endDate } = action;

    try {
        yield put({
            type: FETCH_HRV_DAILY_FETCHING,
            startDate,
            endDate
        });

        const response = yield call(gcApi.get, `hrv-service/hrv/daily/${startDate}/${endDate}`);

        yield put({
            type: FETCH_HRV_DAILY_SUCCESS,
            hrvData: response.data,
            startDate,
            endDate
        });
    } catch (e) {
        yield put({
            type: FETCH_HRV_DAILY_FAIL,
            startDate,
            endDate
        });
    }
}

// Watcher
export const sleepDailySagas = [
    takeEvery(FETCH_DAILY_SLEEP_DATA_WITH_EPOCHS, fetchDailySleepDataWithEpochsSaga),
    takeEvery(UPDATE_SLEEP_NOTE, updateSleepNote),
    takeEvery(ADD_SLEEP, addSleep),
    takeEvery(UPDATE_SLEEP_WINDOW, updateSleepWindow),
    takeEvery(FETCH_HRV_DAILY, fetchHrvDaily),
    takeEvery(FETCH_NAP_BY_DATE, fetchNaps),
    takeEvery(ADD_NAP, addNap),
    takeEvery(DELETE_NAP, deleteNap),
    takeEvery(UPDATE_NAP, updateNap)
];
